import React, { useRef, useEffect } from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Box as MuiBox,
  Typography as MuiTypography,
  TextField as MuiTextField,
  Button as MuiButton,
  IconButton as MuiIconButton,
  Paper as MuiPaper,
  Avatar as MuiAvatar,
  Chip as MuiChip,
  Divider as MuiDivider,
  LinearProgress as MuiLinearProgress,
  Fade as MuiFade,
  CircularProgress as MuiCircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { ChatMessage, InitiativeData } from '../types/index';
import { STRATEGIC_THEMES, TEAM_ROLES } from '../constants/index';

interface InitiativeCreationModalProps {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  onMessagesChange: (messages: ChatMessage[]) => void;
  chatInput: string;
  onChatInputChange: (input: string) => void;
  chatStep: number;
  onSendMessage: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  isLoadingNewInitiative: boolean;
  loadingProgress: number;
  initiativeData: InitiativeData;
  onFileInputRef: React.RefObject<HTMLInputElement>;
  onCreateInitiative: () => void;
}

// Strategic Theme Selection Component
const ThemeSelectionChips: React.FC<{
  onThemeSelect: (theme: string) => void;
  selectedTheme?: string;
}> = ({ onThemeSelect, selectedTheme }) => (
  <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
    {STRATEGIC_THEMES.map((theme) => (
      <MuiChip
        key={theme.name}
        label={theme.name}
        icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
        clickable
        variant={selectedTheme === theme.name ? 'filled' : 'outlined'}
        color={selectedTheme === theme.name ? 'primary' : 'default'}
        onClick={() => onThemeSelect(theme.name)}
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: 1
          }
        }}
      />
    ))}
  </MuiBox>
);

// Team Selection Component
const TeamSelectionChips: React.FC<{
  onTeamSelect: (team: string) => void;
  selectedTeams: string[];
}> = ({ onTeamSelect, selectedTeams }) => (
  <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, maxHeight: 150, overflow: 'auto' }}>
    {TEAM_ROLES.slice(0, 12).map((team) => {
      const isSelected = selectedTeams.includes(team);
      return (
        <MuiChip
          key={team}
          label={team}
          clickable
          variant={isSelected ? 'filled' : 'outlined'}
          color={isSelected ? 'secondary' : 'default'}
          onClick={() => onTeamSelect(team)}
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 1
            }
          }}
        />
      );
    })}
  </MuiBox>
);

// File Upload Zone Component
const FileUploadZone: React.FC<{
  onFileSelect: () => void;
  files: File[];
  isDragOver?: boolean;
}> = ({ onFileSelect, files, isDragOver = false }) => (
  <MuiBox
    onClick={onFileSelect}
    sx={{
      mt: 2,
      p: 3,
      border: '2px dashed',
      borderColor: isDragOver ? 'primary.main' : 'divider',
      borderRadius: 2,
      textAlign: 'center',
      cursor: 'pointer',
      bgcolor: isDragOver ? 'primary.50' : 'background.paper',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: 'primary.50'
      }
    }}
  >
    <UploadIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
    <MuiTypography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
      Drop files here or click to browse
    </MuiTypography>
    {files.length > 0 && (
      <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
        {files.map((file, index) => (
          <MuiChip
            key={index}
            label={file.name}
            icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
      </MuiBox>
    )}
  </MuiBox>
);

export const InitiativeCreationModal: React.FC<InitiativeCreationModalProps> = ({
  open,
  onClose,
  messages,
  onMessagesChange,
  chatInput,
  onChatInputChange,
  chatStep,
  onSendMessage,
  onKeyPress,
  isLoadingNewInitiative,
  loadingProgress,
  initiativeData,
  onFileInputRef,
  onCreateInitiative
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedTheme, setSelectedTheme] = React.useState<string>('');
  const [selectedTeams, setSelectedTeams] = React.useState<string[]>([]);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setSelectedTheme(initiativeData.theme);
    setSelectedTeams(initiativeData.userRoles);
  }, [initiativeData]);

  const handleThemeSelect = (theme: string) => {
    setSelectedTheme(theme);
    onChatInputChange(theme);
  };

  const handleTeamSelect = (team: string) => {
    const newTeams = selectedTeams.includes(team)
      ? selectedTeams.filter(t => t !== team)
      : [...selectedTeams, team];
    setSelectedTeams(newTeams);
    onChatInputChange(newTeams.join(', '));
  };

  const handleFileSelect = () => {
    onFileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    // Handle file drop logic here
  };

  const getStepIndicator = () => {
    const steps = ['Theme', 'Goal', 'Teams', 'Files', 'Review'];
    return (
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: index <= chatStep ? 'primary.main' : 'action.disabled',
                color: index <= chatStep ? 'primary.contrastText' : 'text.disabled',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              {index < chatStep ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : index + 1}
            </MuiBox>
            <MuiTypography
              variant="caption"
              sx={{
                color: index <= chatStep ? 'text.primary' : 'text.disabled',
                fontWeight: index === chatStep ? 600 : 400,
                fontSize: '0.75rem'
              }}
            >
              {step}
            </MuiTypography>
            {index < steps.length - 1 && (
              <MuiDivider
                sx={{
                  width: 20,
                  height: 2,
                  bgcolor: index < chatStep ? 'primary.main' : 'action.disabled'
                }}
              />
            )}
          </React.Fragment>
        ))}
      </MuiBox>
    );
  };

  const renderInteractiveElements = () => {
    switch (chatStep) {
      case 1:
        return (
          <ThemeSelectionChips 
            onThemeSelect={handleThemeSelect}
            selectedTheme={selectedTheme}
          />
        );
      case 3:
        return (
          <TeamSelectionChips
            onTeamSelect={handleTeamSelect}
            selectedTeams={selectedTeams}
          />
        );
      case 4:
        return (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            files={initiativeData.files}
            isDragOver={isDragOver}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <MuiDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: 700,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <MuiDialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: '#ffffff',
            py: 2
          }}
        >
          <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BotIcon sx={{ fontSize: 24 }} />
            <MuiBox>
              <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                Create New Initiative
              </MuiTypography>
              <MuiTypography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                AI-guided initiative setup
              </MuiTypography>
            </MuiBox>
          </MuiBox>
          <MuiIconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#ffffff' }}
          >
            <CloseIcon />
          </MuiIconButton>
        </MuiDialogTitle>

        <MuiDialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Progress Indicator */}
          <MuiBox sx={{ p: 3, pb: 0 }}>
            {getStepIndicator()}
          </MuiBox>

          {/* Chat Messages */}
          <MuiBox
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {messages.map((message) => (
              <MuiBox
                key={message.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <MuiAvatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: message.type === 'ai' ? 'primary.main' : 'secondary.main',
                    fontSize: '0.875rem'
                  }}
                >
                  {message.type === 'ai' ? <BotIcon sx={{ fontSize: 20 }} /> : <PersonIcon sx={{ fontSize: 20 }} />}
                </MuiAvatar>

                <MuiPaper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '75%',
                    bgcolor: message.type === 'user' ? 'primary.50' : 'background.paper',
                    borderRadius: 2,
                    borderTopRightRadius: message.type === 'user' ? 0.5 : 2,
                    borderTopLeftRadius: message.type === 'ai' ? 0.5 : 2
                  }}
                >
                  <MuiTypography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {message.content}
                  </MuiTypography>
                  <MuiTypography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mt: 1,
                      display: 'block'
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </MuiTypography>
                </MuiPaper>
              </MuiBox>
            ))}

            {/* Interactive Elements */}
            {renderInteractiveElements()}

            <div ref={messagesEndRef} />
          </MuiBox>

          {/* Loading State */}
          {isLoadingNewInitiative && (
            <MuiFade in={isLoadingNewInitiative}>
              <MuiBox sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <MuiCircularProgress size={24} />
                  <MuiTypography variant="body2" sx={{ color: 'text.primary' }}>
                    {loadingProgress < 30 ? 'Creating initiative structure...' :
                     loadingProgress < 60 ? 'Generating 4 features...' :
                     loadingProgress < 90 ? 'Setting up SDLC phases...' :
                     'Finalizing setup...'}
                  </MuiTypography>
                </MuiBox>
                <MuiLinearProgress 
                  variant="determinate" 
                  value={loadingProgress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3
                    }
                  }}
                />
                <MuiTypography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    mt: 1, 
                    display: 'block',
                    textAlign: 'center'
                  }}
                >
                  {loadingProgress}% complete
                </MuiTypography>
                {loadingProgress >= 60 && loadingProgress < 90 && (
                  <MuiBox sx={{ mt: 2 }}>
                    <MuiTypography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center' }}>
                      Features: Fea 01 • Fea 02 • Fea 03 • Fea 04
                    </MuiTypography>
                  </MuiBox>
                )}
              </MuiBox>
            </MuiFade>
          )}
        </MuiDialogContent>

        <MuiDialogActions
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2
          }}
        >
          {chatStep === 5 ? (
            <>
              <MuiButton
                variant="outlined"
                onClick={onClose}
                disabled={isLoadingNewInitiative}
              >
                Cancel
              </MuiButton>
              <MuiButton
                variant="contained"
                onClick={onCreateInitiative}
                disabled={isLoadingNewInitiative}
                startIcon={isLoadingNewInitiative ? <MuiCircularProgress size={16} /> : <CheckCircleIcon />}
              >
                {isLoadingNewInitiative ? 'Creating...' : 'Create Initiative'}
              </MuiButton>
            </>
          ) : (
            <MuiBox sx={{ flex: 1, display: 'flex', gap: 1 }}>
              <MuiTextField
                fullWidth
                multiline
                maxRows={2}
                value={chatInput}
                onChange={(e) => onChatInputChange(e.target.value)}
                onKeyPress={onKeyPress}
                placeholder="Type your response..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <MuiButton
                variant="contained"
                onClick={onSendMessage}
                disabled={!chatInput.trim()}
                sx={{
                  minWidth: 48,
                  px: 2
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </MuiButton>
            </MuiBox>
          )}
        </MuiDialogActions>
      </MuiDialog>

      {/* Hidden file input */}
      <input
        ref={onFileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
      />
    </>
  );
};

export default InitiativeCreationModal;